export interface OnColorStrategyOptions {
  lightText?: string
  darkText?: string
  minContrast?: number
}

let defaultOnColorOptions: Required<OnColorStrategyOptions> = {
  lightText: '#ffffff',
  darkText: '#0b0f13',
  minContrast: 4.5, // Target WCAG AA for normal text
}

export function setOnColorStrategyOptions(options: OnColorStrategyOptions) {
  defaultOnColorOptions = {
    ...defaultOnColorOptions,
    ...options,
  }
}

export function pickOnColor(backgroundColor: string, options?: OnColorStrategyOptions): string {
  const { lightText, darkText } = { ...defaultOnColorOptions, ...options }
  const bg = parseColor(backgroundColor)
  if (!bg) return lightText

  const contrastWithLight = contrastRatio(bg, parseColor(lightText)!)
  const contrastWithDark = contrastRatio(bg, parseColor(darkText)!)
  return contrastWithLight >= contrastWithDark ? lightText : darkText
}

interface RGB {
  r: number
  g: number
  b: number
}

function parseColor(input: string): RGB | null {
  if (!input) return null
  const s = input.trim().toLowerCase()
  // #rgb, #rrggbb, or #rrggbbaa (alpha ignored)
  if (s[0] === '#') {
    const hex = s.slice(1)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16)
      const g = parseInt(hex[1] + hex[1], 16)
      const b = parseInt(hex[2] + hex[2], 16)
      return { r, g, b }
    }
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return { r, g, b }
    }
    return null
  }
  // rgb(r, g, b)
  const rgbMatch = s.match(/^rgb\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\)$/)
  if (rgbMatch) {
    const r = clamp255(parseInt(rgbMatch[1], 10))
    const g = clamp255(parseInt(rgbMatch[2], 10))
    const b = clamp255(parseInt(rgbMatch[3], 10))
    return { r, g, b }
  }
  return null
}

function clamp255(n: number): number {
  return Math.min(255, Math.max(0, n))
}

function srgbToLinear(c: number): number {
  const cs = c / 255
  return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4)
}

function relativeLuminance(rgb: RGB): number {
  const r = srgbToLinear(rgb.r)
  const g = srgbToLinear(rgb.g)
  const b = srgbToLinear(rgb.b)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastRatio(a: RGB, b: RGB): number {
  const L1 = relativeLuminance(a)
  const L2 = relativeLuminance(b)
  const lighter = Math.max(L1, L2)
  const darker = Math.min(L1, L2)
  return (lighter + 0.05) / (darker + 0.05)
}

export type ThemeMode = 'light' | 'dark'

function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(clamp255(rgb.r))}${toHex(clamp255(rgb.g))}${toHex(clamp255(rgb.b))}`
}

function mixRgb(colorA: RGB, colorB: RGB, weightA: number): RGB {
  const wA = Math.min(1, Math.max(0, weightA))
  const wB = 1 - wA
  return {
    r: Math.round(colorA.r * wA + colorB.r * wB),
    g: Math.round(colorA.g * wA + colorB.g * wB),
    b: Math.round(colorA.b * wA + colorB.b * wB),
  }
}

export function generateScale(baseColor: string, mode: ThemeMode): Record<number, string> {
  const base = parseColor(baseColor) || { r: 0, g: 0, b: 0 }
  const white = { r: 255, g: 255, b: 255 }
  const black = { r: 0, g: 0, b: 0 }

  const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const
  const result: Record<number, string> = {}

  for (const n of steps) {
    let mixed: RGB
    if (n === 500) {
      // 500 is always the base color itself
      mixed = base
    } else if (mode === 'light') {
      if (n < 500) {
        // lighten towards white: 5%,10%,20%,30%,40% brand
        const brandPct = n === 50 ? 0.05 : n === 100 ? 0.1 : n === 200 ? 0.2 : n === 300 ? 0.3 : 0.4
        mixed = mixRgb(base, white, brandPct)
      } else {
        // darken towards black: 90%,80%,70%,60% brand
        const brandPct = n === 600 ? 0.9 : n === 700 ? 0.8 : n === 800 ? 0.7 : 0.6
        mixed = mixRgb(base, black, brandPct)
      }
    } else {
      // dark mode: invert logic
      if (n < 500) {
        const brandPct = n === 50 ? 0.05 : n === 100 ? 0.1 : n === 200 ? 0.2 : n === 300 ? 0.3 : 0.4
        mixed = mixRgb(base, black, brandPct)
      } else {
        const brandPct = n === 600 ? 0.9 : n === 700 ? 0.8 : n === 800 ? 0.7 : 0.6
        mixed = mixRgb(base, white, brandPct)
      }
    }
    result[n] = rgbToHex(mixed)
  }
  return result
}

export function applyScale(root: HTMLElement, tokenPrefix: string, scale: Record<number, string>) {
  for (const [key, value] of Object.entries(scale)) {
    root.style.setProperty(`${tokenPrefix}-${key}`, value)
  }
}

/**
 * Derives a surface colour from a given background hex so that all floating
 * UI (dropdowns, modals, cards, inputs) feel visually consistent with the
 * active theme even when the admin has not explicitly picked a surface colour.
 *
 * Strategy:
 *  • Dark background  (luminance < 0.12) → lift with ~10% white overlay
 *  • Medium background (luminance 0.12–0.5) → lift with ~5% white overlay
 *  • Light background (luminance > 0.5)  → tint with ~3% black overlay
 *    (produces the canonical off-white surface for white-background themes)
 */
